import { Spinner, SpinnerSize } from '@fluentui/react';
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { logService, telemetryService } from 'npo-common';
import { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import localeService from 'src/dataServices/LocaleService';
import { IApplicationState } from 'src/state';
import { CustomPageView, PageNames, PageRenderingErrorMessage } from '../../common/Constants';
import Unknown from '../../components/error/Unknown';
import EligibilityPreCheckSection from './EligibilityPreCheckSection';
import GettingStartedTopBanner from './GettingStartedTopBanner';
import './GettingStartedV2.scss';
import GrantsSection from './GrantsSection';
import SignInSection from './SignInSection';

interface IPropsFromState {
  isAuthenticated: boolean;
}

const isUhfContentLoaded = () => {
  const header = document.getElementById('uhfHeader');
  const footer = document.getElementById('uhfFooter');

  return (header?.innerHTML?.trim().length ?? 0) > 0 || (footer?.innerHTML?.trim().length ?? 0) > 0;
};

const GettingStartedV2 = (props: IPropsFromState) => {
  const featureFlags = useSelector((state: IApplicationState) => state.flags);
  const eligibilityPreCheckSectionEnabled = featureFlags.EligibilityPreCheckSectionEnabled;
  const [UFHLoading, setUHFLoading] = useState(!isUhfContentLoaded());

  // runs UHF scripts on mount for getting started page
  useEffect(() => {
    const removeFocusableHeadings = () => {
      const rogue = document.querySelectorAll('.c-uhf-sronly');
      rogue.forEach((el) => {
        el.removeAttribute('tabIndex');
        el.setAttribute('tabIndex', '-1');
      });

      setUHFLoading(false);
    };

    window.addEventListener('UHFDoneLoading', removeFocusableHeadings);

    const globalWindow = window as Window & { __nppUhfLoadTriggered?: boolean };
    if (isUhfContentLoaded()) {
      setUHFLoading(false);
      removeFocusableHeadings();
    } else if (!globalWindow.__nppUhfLoadTriggered) {
      globalWindow.__nppUhfLoadTriggered = true;
      window.dispatchEvent(new Event('loadUHF'));
    }

    logService.logInfo(CustomPageView, {
      pageName: PageNames.GettingStarted,
      locale: localeService.getlocale()
    });

    return () => {
      window.removeEventListener('UHFDoneLoading', removeFocusableHeadings);
    };
  }, []);

  if (UFHLoading) {
    return (
      <Spinner
        size={SpinnerSize.large}
        label={localeService.getText('Shared', 'SpinnerPleaseWait')}
        ariaLive="assertive"
      />
    );
  }

  return (
    <AppInsightsErrorBoundary
      onError={() => {
        logService.logError(PageRenderingErrorMessage, { pageName: PageNames.GettingStarted }, { console: true });
        return <Unknown />;
      }}
      appInsights={telemetryService.aiReactPlugin}
    >
      <div className="getting-started-v2-page">
        <div className="col p-0">
          <div className="px-5 py-2 getting-started-background d-block d-md-none" />
          <GettingStartedTopBanner />
        </div>
        <div className="px-5 py-2 getting-started-background" />
        {eligibilityPreCheckSectionEnabled && (
          <>
            <div className="col p-0">
              <EligibilityPreCheckSection />
            </div>
            <div className="px-5 py-2 getting-started-background" />
          </>
        )}
        <div className="row row-cols-md-2 row-cols-1">
          <div className="col h-auto p-0 pb-3 pr-md-2 pb-md-0">
            <GrantsSection />
            <div className="px-5 py-2 getting-started-background" />
          </div>
          <div className="col h-auto p-0 pb-3 pl-md-2 pb-md-0">
            <SignInSection />
            <div className="px-5 py-2 getting-started-background" />
          </div>
        </div>
      </div>
    </AppInsightsErrorBoundary>
  );
};

const mapStateToProps = ({ auth }: IApplicationState) => ({
  isAuthenticated: auth.isAuthenticated
});

export default connect(mapStateToProps)(GettingStartedV2);
